import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useProducts } from '@/hooks/api/0390_products';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useBillingTargets } from '@/hooks/api/1200_billing_targets';
import { useSalesOrdersInvalidate } from '@/hooks/api/1210_sales_orders';
import { useUpdateSalesOrder } from '@/hooks/api/1230_update_sales_order';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';
import { useInvoiceTasks } from '@/hooks/api/1800_invoice_tasks';
import { BillingTarget, BusinessPartner, Product, User } from '@/hooks/api/models';
import { ProjectLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';

type FormInputs = {
  identifier: string;
  team_id: number;
  id: number;
  name: string;
  description: string;
  user_id: number;
  business_partner_id: number;
  agency_id?: number;
  person_in_charge?: string;
  billing_target_id: number;
  product_id: number;
  spot_amount?: number;
  spot_billing_date?: string;
  monthly_amount?: number;
  monthly_billing_start_date?: string;
  monthly_billing_end_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  delivery_date?: string;
  task_procedure_manual?: string;
};

type Props = {
  targetUsers: User[];
  businessPartners: BusinessPartner[];
  billingTargets: BillingTarget[];
  products: Product[];
  invoiceTaskExists: boolean;
};

export const SalesOrderEdit = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_, set_so_ } = useCurrentSalesOrder();

  const { data: targetUsers } = useSalesOrderTargetUsers({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { data: businessPartnersData } = useBusinessPartners({
    identifier: _w_!.identifier,
    display_type: 'sales',
    page: 1,
    per: 1000,
  });
  const { data: billingTargets } = useBillingTargets();
  const { data: products } = useProducts({ identifier: _w_!.identifier });
  const { data: invoiceTaskRes } = useInvoiceTasks({
    identifier: _w_!.identifier,
    team_ids: [_t_!.id],
    sales_order_id: _so_!.id,
  });

  const [businessPartners, setBusinessPartners] = useState<BusinessPartner[] | null>(null);
  const [invoiceTaskExists, setInvoiceTaskExists] = useState<boolean | null>(null);

  useEffect(() => {
    if (businessPartnersData) setBusinessPartners(() => businessPartnersData.business_partners);
  }, [businessPartnersData]);

  useEffect(() => {
    if (invoiceTaskRes) setInvoiceTaskExists(invoiceTaskRes.pager.total_pages > 0);
  }, [invoiceTaskRes]);

  if (
    targetUsers == null ||
    businessPartners == null ||
    billingTargets == null ||
    products == null ||
    invoiceTaskExists == null
  )
    return <></>;

  return (
    <_SalesOrderEdit
      targetUsers={targetUsers}
      businessPartners={businessPartners}
      billingTargets={billingTargets}
      products={products}
      invoiceTaskExists={invoiceTaskExists}
    />
  );
};

export const _SalesOrderEdit = ({
  targetUsers,
  businessPartners,
  billingTargets,
  products,
  invoiceTaskExists,
}: Props) => {
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();
  const { _so_, set_so_ } = useCurrentSalesOrder();
  useEffect(() => setTitle(`プロジェクト（売上）編集`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `プロジェクト（売上） - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
        { label: `プロジェクト（売上）詳細`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}` },
      ]),
    []
  );

  const location = useLocation();
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { mutate, data, isPending, error } = useUpdateSalesOrder();
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const { invalidate } = useSalesOrdersInvalidate(_w_!.identifier, _t_!.id);
  const [businessPartnerOptions, setBusinessPartnerOptions] = useState<SelectOptionType[]>(() =>
    businessPartners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name }))
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      id: _so_!.id,
    },
  });
  const [productId, monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate] = watch([
    'product_id',
    'monthly_amount',
    'monthly_billing_start_date',
    'monthly_billing_end_date',
  ]);

  // TODO 後から代理店無効になった場合
  // TODO 後から売上カテゴリの設定が変更された場合
  useEffect(() => {
    setValue('name', _so_!.name);
    setValue('description', _so_!.description || '');
    setValue('user_id', _so_!.user.id);
    setValue('business_partner_id', _so_!.business_partner.id);
    setValue('person_in_charge', _so_!.person_in_charge);
    setValue('product_id', _so_!.product.id);
    setValue('contract_start_date', _so_!.contract_start_date?.replace(/-/g, '/'));
    setValue('contract_end_date', _so_!.contract_end_date?.replace(/-/g, '/'));
    if (_t_!.agency_enabled) {
      setValue('agency_id', _so_!.agency?.id);
      setValue('billing_target_id', _so_!.billing_target.id);
    } else {
      setValue('billing_target_id', billingTargets[0].id);
    }
    if (_so_!.product.spot_available) {
      setValue('spot_amount', _so_!.spot_amount);
      setValue('spot_billing_date', _so_!.spot_billing_date?.replace(/-/g, '/'));
    }
    if (_so_!.product.monthly_available) {
      setValue('monthly_amount', _so_!.monthly_amount);
      setValue('monthly_billing_start_date', _so_!.monthly_billing_start_date?.replace(/-/g, '/'));
      setValue('monthly_billing_end_date', _so_!.monthly_billing_end_date?.replace(/-/g, '/'));
    }
    if (_so_!.product.delivery_date_available) {
      setValue('delivery_date', _so_!.delivery_date?.replace(/-/g, '/'));
    }
    setValue('task_procedure_manual', _so_!.task_procedure_manual);
  }, []);

  const isEditable = useMemo(() => {
    if (meTeamMember && TeamMemberLogic.hasApproverRole(meTeamMember)) {
      return true;
    } else {
      return !_so_!.closed;
    }
  }, [meTeamMember, _so_]);

  useEffect(() => {
    if (productId) {
      setSelectedProduct(products!.find((p) => p.id == productId));
    }
  }, [productId]);

  useEffect(() => {
    if (selectedProduct && selectedProduct.id != productId) {
      if (selectedProduct.spot_available) {
        setValue('spot_amount', selectedProduct.spot_amount);
      }
      if (selectedProduct.monthly_available) {
        setValue('monthly_amount', selectedProduct.monthly_amount);
      }
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (monthlyAmount && monthlyBillingStartDate && monthlyBillingEndDate) {
      setTotalAmount((_) =>
        ProjectLogic.calcTotalAmount(monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate)
      );
    } else {
      setTotalAmount((_) => 0);
    }
  }, [monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      set_so_(data);
      toastSuccess('プロジェクト（売上）の編集が完了しました');
      if (location?.state?.prevPath) {
        // navigate(location?.state?.prevPath);
        navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${data.id}/billing_schedules`, {
          state: { prevPath: location?.state?.prevPath },
        });
      } else {
        // navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${data.id}`);
        navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${data.id}/billing_schedules`);
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const [doSubmit, setDoSubmit] = useState(false);
  const [formInputData, setFormInputData] = useState<FormInputs>();

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setFormInputData((_) => data);

    // Product
    // SpotPrice, PostingDate
    // MonthlyFee, BillingStartDate, BillingEndDate
    // に変更が入った際は確認ダイアログを表示
    let showConfirm = false;
    if (_so_!.product.id !== getValues('product_id')) {
      showConfirm = true;
    } else {
      if (_so_!.product.spot_available) {
        if (_so_!.spot_amount !== getValues('spot_amount')) showConfirm = true;
        if (_so_!.spot_billing_date?.replace(/-/g, '/') !== getValues('spot_billing_date')) showConfirm = true;
      }

      if (_so_!.product.monthly_available) {
        if (_so_!.monthly_amount !== getValues('monthly_amount')) showConfirm = true;
        if (_so_!.monthly_billing_start_date?.replace(/-/g, '/') !== getValues('monthly_billing_start_date'))
          showConfirm = true;
        if (_so_!.monthly_billing_end_date?.replace(/-/g, '/') !== getValues('monthly_billing_end_date'))
          showConfirm = true;
      }
    }

    if (showConfirm) {
      setDoSubmit(false);
      document.getElementById('edit-confirm-button')?.click();
    } else {
      setDoSubmit(true);
      onEditClick();
    }
  };

  useEffect(() => {
    if (formInputData && doSubmit) {
      setShowLoading(true);
      mutate(formInputData!);
    }
  }, [formInputData, doSubmit]);

  const onEditClick = () => {
    setDoSubmit(true);
  };

  const onBusinessPartnerChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('business_partner_id', sv.value);
      trigger('business_partner_id');
    } else {
      resetField('business_partner_id');
    }
  };

  const onAgencyChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('agency_id', sv.value);
      trigger('agency_id');
    } else {
      resetField('agency_id');
    }
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <ModalConfirm
        modalId={`editConfirm`}
        okClicked={() => onEditClick()}
        cancelClicked={() => {
          setFormInputData(undefined);
          setDoSubmit(false);
        }}
        message={
          'プロジェクトの請求金額にかかわる箇所の変更を行います。\nそのため、変更済の請求計画がリセットされ再設定されます。\n\nよろしいですか？'
        }
      />
      <button
        data-bs-toggle="modal"
        data-bs-target={`#editConfirm`}
        style={{ display: 'none' }}
        id="edit-confirm-button"
      />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">プロジェクト（売上）編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="mb-3">
                    <div className="mb-0 font-weight-bold">プロジェクト基本情報</div>
                  </div>
                  <FormGroup label="プロジェクト名" required={true}>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'案件名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </FormGroup>

                  <FormGroup label="メモ">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('description')}
                    ></textarea>
                  </FormGroup>

                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="プロジェクト担当者" required={true}>
                        <select className="form-control" required={true} {...register('user_id')}>
                          {targetUsers.map((tu, i) => (
                            <option key={`target-user-${tu.id}`} value={tu.id}>
                              {tu.user_account.name}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="取引先" required={true}>
                      <Controller
                        control={control}
                        name="business_partner_id"
                        rules={{ required: true }}
                        render={({ field: { ref, ...field } }) => (
                          <SelectSingle
                            options={businessPartnerOptions}
                            selectedId={getValues('business_partner_id')}
                            onChange={onBusinessPartnerChange}
                            error={errors.business_partner_id != null}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="取引先担当者">
                      <input type="text" className="form-control" {...register('person_in_charge')} />
                    </FormGroup>
                  </div>

                  {_t_!.agency_enabled && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="代理店">
                          <Controller
                            control={control}
                            name="agency_id"
                            rules={{}}
                            render={({ field: { ref, ...field } }) => (
                              <SelectSingle
                                options={businessPartnerOptions}
                                selectedId={getValues('agency_id')}
                                onChange={onAgencyChange}
                              />
                            )}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-12 col-md-6">
                        <FormGroup label="請求先" required={true}>
                          <select className="form-control" required={true} {...register('billing_target_id')}>
                            {billingTargets!.map((bt, i) => (
                              <option key={`bt-${bt.id}`} value={bt.id}>
                                {bt.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </div>
                    </>
                  )}
                  <div className="my-3">
                    <div className="mb-1 font-weight-bold">プロジェクト詳細情報</div>
                    <p className="text-xs mb-0">契約タスク承認後は承認者のみが金額や期間の変更を行えます</p>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="売上カテゴリ" required={true}>
                        <select
                          className="form-control"
                          required={true}
                          {...register('product_id')}
                          disabled={!isEditable}
                        >
                          {products!.map((p, i) => (
                            <option key={`product-${p.id}`} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  {selectedProduct?.spot_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="一括請求金額（税抜）" required={true}>
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice
                                  onChange={onChange}
                                  value={value}
                                  error={errors.spot_amount}
                                  disabled={!isEditable}
                                />
                              )}
                              control={control}
                              name="spot_amount"
                              rules={{ required: true }}
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">納品時等一括で請求する金額</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求日" required={true}>
                          <Controller
                            control={control}
                            name="spot_billing_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.spot_billing_date && 'is-invalid'}`}
                                {...field}
                                disabled={!isEditable}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, spotBillingDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={(e) => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct?.monthly_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="月額請求金額（税抜）" required={true}>
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice
                                  onChange={onChange}
                                  value={value}
                                  error={errors.monthly_amount}
                                  disabled={!isEditable}
                                />
                              )}
                              control={control}
                              name="monthly_amount"
                              rules={{ required: true }}
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">&nbsp;</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="月額合計金額">
                          <input
                            type="text"
                            className="form-control text-end"
                            value={`¥${totalAmount.toLocaleString()}`}
                            disabled
                          />
                          <div className="text-xs text-muted mt-1">自動計算されます</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求対象期間の開始日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_start_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${
                                  errors.monthly_billing_start_date && 'is-invalid'
                                }`}
                                {...field}
                                onChange={(_, dateString) => {
                                  // monthly_billing_start_dateと契約開始日が同じなら上書き
                                  const curMonthlyBillingStartDate = getValues('monthly_billing_start_date');
                                  const curContractStartDate = getValues('contract_start_date');
                                  if (
                                    curContractStartDate == null ||
                                    curMonthlyBillingStartDate == curContractStartDate
                                  ) {
                                    setValue('contract_start_date', dateString);
                                  }

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                                disabled={!isEditable}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の開始日</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求対象期間の終了日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_end_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.monthly_billing_end_date && 'is-invalid'}`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, monthlyBillingEndDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                                disabled={!isEditable}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の終了日</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="契約開始日">
                          <Controller
                            control={control}
                            name="contract_start_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約開始となる日付</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="契約終了日">
                          <Controller
                            control={control}
                            name="contract_end_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約終了となる日付</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct?.delivery_date_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="納品日">
                          <Controller
                            control={control}
                            name="delivery_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, deliveryDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の納品の日付</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  <FormGroup label="請求タスク処理マニュアル">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('task_procedure_manual')}
                    ></textarea>
                    <div className="text-xs text-muted mt-1">請求タスクに連携される事務処理手順</div>
                  </FormGroup>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
